import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { useQuery } from '@tanstack/react-query';
import { contentApi } from '@/services/adminApi';
import type { Content } from '@/types/admin';

export const PrivacyPage: React.FC = () => {
  const PRIVACY_CONTENT_ID = 'CNT-five_ai_privacy_policy_V1';

  const fallback = useMemo(
    () => ({
      title: 'Privacy Policy',
      description: 'How Five AI collects, uses, and protects your information.',
      lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      sections: [
        {
          id: 'collection',
          title: '1. Information We Collect',
          content:
            'We collect information you provide directly to us, including account details (name, email address, password), professional profile data (learning goals, skills, career preferences), and communications you send to us.\n\nWe also automatically collect usage data (how you interact with the AI coach, learning paths, and platform features), technical data (device type, browser, IP address, operating system), and cookies and similar tracking technologies as described in our Cookie Policy.\n\nAll data collection is limited to what is necessary to deliver, personalize, and improve our services.',
        },
        {
          id: 'use',
          title: '2. How We Use Your Information',
          content:
            'We use your information to:\n\n- Provide and personalize your AI coaching and learning experience\n- Generate skill assessments, career recommendations, and learning paths\n- Communicate with you about your account, updates, and relevant features\n- Improve our platform, algorithms, and service quality\n- Ensure security, prevent fraud, and comply with legal obligations\n- Process payments and manage subscriptions\n- Conduct analytics and research to enhance our services\n\nWe process your data based on contractual necessity (to deliver the service you signed up for), your consent (where explicitly obtained), and our legitimate interests (product improvement, security) balanced against your rights.',
        },
        {
          id: 'sharing',
          title: '3. Information Sharing and Disclosure',
          content:
            '**We do not sell your personal data.**\n\nWe may share your information with:\n\n- **Service providers**: Cloud hosting, analytics, payment processing, and email delivery partners who assist in operating our platform, under strict confidentiality and data processing agreements\n- **AI model providers**: Anonymized or pseudonymized data may be sent to AI providers to deliver coaching features; we do not share identifiable personal data with AI model providers for their own training purposes\n- **Legal requirements**: When required by law, regulation, legal process, or governmental request\n- **Business transfers**: In connection with a merger, acquisition, or sale of assets, with prior notice to you\n\nAll third-party service providers are contractually obligated to protect your data and use it only for the purposes we specify.',
        },
        {
          id: 'rights',
          title: '4. Your Rights',
          content:
            'Depending on your jurisdiction, you have the right to:\n\n- **Access**: Request a copy of the personal data we hold about you\n- **Rectification**: Correct inaccurate or incomplete data\n- **Erasure**: Request deletion of your personal data (subject to legal retention requirements)\n- **Restriction**: Request that we limit the processing of your data\n- **Portability**: Receive your data in a structured, machine-readable format\n- **Objection**: Object to processing based on legitimate interests\n- **Withdraw consent**: Where processing is based on consent, withdraw it at any time\n\nYou can exercise many of these rights through your account settings. For other requests, contact our Data Protection Officer at **dpo@fiveai.co** or our support team at **support@fiveai.co**.',
        },
        {
          id: 'security',
          title: '5. Data Security',
          content:
            'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:\n\n- Encryption of data in transit (TLS 1.2+) and at rest (AES-256)\n- Regular security assessments and penetration testing\n- Role-based access controls and audit logging\n- Secure development practices and code review\n- Incident response procedures and breach notification protocols\n\nWhile we strive to protect your data, no method of transmission or storage is 100% secure. See our Security page for more details.',
        },
        {
          id: 'retention',
          title: '6. Data Retention',
          content:
            'We retain your personal data only as long as necessary to fulfill the purposes for which it was collected, including:\n\n- **Active account data**: Retained while your account is active and for a reasonable period after account closure\n- **Usage and analytics data**: Retained in anonymized/aggregated form for product improvement\n- **Legal and compliance data**: Retained as required by applicable laws (tax, accounting, legal claims)\n- **Communication records**: Retained for customer support quality and dispute resolution\n\nYou may request deletion of your data at any time, subject to applicable legal retention requirements.',
        },
        {
          id: 'cookies',
          title: '7. Cookies and Tracking',
          content:
            'We use cookies and similar technologies for essential functionality (authentication, security, preferences), analytics (understanding how you use our platform), and personalization (tailoring your experience).\n\nYou can manage cookie preferences through your browser settings or our cookie consent banner. Some features may not function properly if you disable certain cookies. For more details, see our Cookie Policy.',
        },
        {
          id: 'children',
          title: '8. Children\'s Privacy',
          content:
            'Our services are not directed to individuals under the age of 16. We do not knowingly collect personal data from children. If we become aware that we have collected data from a child without parental consent, we will take steps to delete that information promptly. If you believe a child has provided us with personal data, please contact us at **support@fiveai.co**.',
        },
        {
          id: 'international',
          title: '9. International Data Transfers',
          content:
            'Your data may be processed in countries other than your own. Where we transfer personal data outside the European Economic Area (EEA), United Kingdom, or other jurisdictions with data transfer restrictions, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission, adequacy decisions, and other mechanisms recognized by applicable law.',
        },
        {
          id: 'updates',
          title: '10. Changes to This Policy',
          content:
            'We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes via email or in-app notice at least 30 days before they take effect. Continued use of our services after changes constitutes acceptance of the updated policy.\n\nThe "Last updated" date at the top of this page indicates when this policy was last revised.',
        },
        {
          id: 'contact',
          title: '11. Contact Information',
          content:
            'If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:\n\n**UnboundXLabs Private Limited**\nBangalore 560068, India\n\n- **Data Protection Officer**: dpo@fiveai.co\n- **General Support**: support@fiveai.co\n- **Legal Inquiries**: legal@fiveai.co\n\nWe aim to respond to all privacy-related inquiries within 30 days.',
        },
      ],
    }),
    []
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-privacy', PRIVACY_CONTENT_ID],
    queryFn: () => contentApi.getByContentId(PRIVACY_CONTENT_ID),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

  const resolved = useMemo(() => {
    const content: Content | null = data ?? null;
    const metadata = content?.metadata;
    const privacyContent = isRecord(metadata) && isRecord(metadata.privacyContent) ? metadata.privacyContent : null;
    const intro = privacyContent && isRecord(privacyContent.introduction) ? privacyContent.introduction : null;
    const sectionsRaw = privacyContent && Array.isArray(privacyContent.sections) ? (privacyContent.sections as unknown[]) : [];
    const footer = privacyContent && isRecord(privacyContent.footer) ? privacyContent.footer : null;

    const lastUpdated =
      isRecord(metadata) && typeof metadata.lastUpdated === 'string'
        ? metadata.lastUpdated
        : content?.updatedAt
          ? new Date(content.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : fallback.lastUpdated;

    const sections =
      sectionsRaw
        .map((s, idx) => {
          const sec = isRecord(s) ? s : null;
          const id = sec && typeof sec.id === 'string' ? sec.id : `section-${idx + 1}`;
          const title = sec && typeof sec.title === 'string' ? sec.title : `Section ${idx + 1}`;
          const contentText = sec && typeof sec.content === 'string' ? sec.content : '';
          return { id, title, content: contentText };
        })
        .filter((s) => s.title || s.content) || [];

    const headerTitle = content?.title || fallback.title;
    const headerDescription = content?.description || fallback.description;

    const introTitle = intro && typeof intro.title === 'string' ? intro.title : undefined;
    const introText = intro && typeof intro.text === 'string' ? intro.text : undefined;

    const footerTitle = footer && typeof footer.title === 'string' ? footer.title : undefined;
    const footerContent = footer && typeof footer.content === 'string' ? footer.content : undefined;

    return {
      isFallback: !content || !privacyContent,
      headerTitle,
      headerDescription,
      lastUpdated,
      introTitle,
      introText,
      sections: sections.length > 0 ? sections : fallback.sections,
      footerTitle,
      footerContent,
    };
  }, [data, fallback]);

  useEffect(() => {
    document.title = resolved.headerTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && resolved.headerDescription) {
      meta.setAttribute('content', resolved.headerDescription);
    }
  }, [resolved.headerTitle, resolved.headerDescription]);

  const renderInline = (text: string) => {
    const boldParts = text.split(/\*\*(.*?)\*\*/g);
    return boldParts.map((part, idx) => {
      const isBold = idx % 2 === 1;
      const urlParts = part.split(/(https?:\/\/[^\s)]+)\b/g);
      const nodes = urlParts.map((p, j) => {
        const isUrl = /^https?:\/\//.test(p);
        if (!isUrl) return <React.Fragment key={`${idx}-${j}`}>{p}</React.Fragment>;
        return (
          <a
            key={`${idx}-${j}`}
            href={p}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {p}
          </a>
        );
      });
      return isBold ? <strong key={idx}>{nodes}</strong> : <span key={idx}>{nodes}</span>;
    });
  };

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Privacy Policy">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Legal</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Privacy</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  Policy
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                {resolved.headerDescription || 'How Five AI collects, uses, and protects your information.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground/80">
                <span>Last updated: {resolved.lastUpdated}</span>
                {isLoading && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                    <BrandedSpinner sizeClassName="h-3 w-3" />
                    Loading...
                  </span>
                )}
                {isError && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-xs">
                    Showing fallback
                  </span>
                )}
                {resolved.isFallback && !isLoading && !isError && (
                  <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
                    Default content
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-[1fr_240px]">
              <div className="min-w-0">
                {(resolved.introTitle || resolved.introText) && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-10"
                  >
                    <Card className="rounded-2xl border-2 bg-gradient-to-br from-primary/5 to-violet-500/5 p-6 sm:p-8">
                      {resolved.introTitle && (
                        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
                          {resolved.introTitle}
                        </h2>
                      )}
                      {resolved.introText && (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {renderInline(resolved.introText)}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}

                <div className="space-y-10">
                  {resolved.sections.map((s, i) => (
                    <motion.section
                      key={s.id}
                      id={s.id}
                      className="scroll-mt-28"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-50px' }}
                      transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
                    >
                      <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
                        {s.title}
                      </h2>
                      <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {renderInline(s.content)}
                      </div>
                    </motion.section>
                  ))}
                </div>

                {(resolved.footerTitle || resolved.footerContent) && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-10"
                  >
                    <Card className="rounded-2xl border-2 bg-muted/30 p-6 sm:p-8">
                      {resolved.footerTitle && (
                        <h2 className="text-xl font-bold text-foreground tracking-tight mb-3">
                          {resolved.footerTitle}
                        </h2>
                      )}
                      {resolved.footerContent && (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {renderInline(resolved.footerContent)}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}
              </div>

              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <Card className="rounded-2xl border-2 p-6 bg-card">
                    <CardHeader className="p-0 mb-4">
                      <CardTitle className="text-base font-semibold text-foreground">On this page</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <nav className="space-y-2" aria-label="Privacy policy sections">
                        {resolved.sections.map((s) => (
                          <a
                            key={s.id}
                            href={`#${s.id}`}
                            className="block text-sm text-muted-foreground hover:text-primary hover:underline transition-colors py-0.5"
                          >
                            {s.title}
                          </a>
                        ))}
                      </nav>
                    </CardContent>
                  </Card>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
